import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchData } from "../../services/apiService";
import {
  Wifi,
  Zap,
  Clock,
  LogOut,
  Loader2,
  Signal,
  CheckCircle2,
  Copy,
  Check,
  CreditCard,
  RefreshCw,
} from "lucide-react";

const PortalPlans: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 1. Capture Params
  const linkLogin = searchParams.get("link_login");
  const linkLoginOnly = searchParams.get("link_login_only");
  const linkOrig = searchParams.get("link_orig");
  const routerId =
    searchParams.get("router_id") || sessionStorage.getItem("hs_router_id");
  const uamip =
    searchParams.get("uamip") ||
    sessionStorage.getItem("mt_uamip") ||
    "10.5.50.1";
  const mac = searchParams.get("mac") || sessionStorage.getItem("mt_mac") || "";

  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const PAYBILL = "552800";
  const ACCOUNT = "411591";

  const user = JSON.parse(sessionStorage.getItem("hs_user") || "null");

  // 2. Persist Params
  useEffect(() => {
    if (linkLogin) sessionStorage.setItem("mt_link_login", linkLogin);
    if (linkLoginOnly)
      sessionStorage.setItem("mt_link_login_only", linkLoginOnly);
    if (linkOrig) sessionStorage.setItem("mt_link_orig", linkOrig);
    if (mac) sessionStorage.setItem("mt_mac", mac);
    if (uamip) sessionStorage.setItem("mt_uamip", uamip);
    if (routerId) sessionStorage.setItem("hs_router_id", String(routerId));
  }, [linkLogin, linkLoginOnly, linkOrig, mac, uamip, routerId]);

  const queryStr = `?mac=${encodeURIComponent(
    mac || "",
  )}&uamip=${encodeURIComponent(uamip || "")}`;

  // 3. Redirect Function
  const redirectToMikrotikConnect = (token: string) => {
    let routerIp = sessionStorage.getItem("mt_uamip");

    if (!routerIp || routerIp === "undefined") {
      const savedLink = sessionStorage.getItem("mt_link_login");
      if (savedLink) {
        try {
          routerIp = new URL(savedLink).hostname;
        } catch (e) {
          routerIp = "10.5.50.1";
        }
      } else {
        routerIp = "10.5.50.1";
      }
    }
    window.location.href = `http://${routerIp}/connect.html?t=${encodeURIComponent(
      token,
    )}&host=${routerIp}`;
  };

  const fmtSpeed = (kbps: number) =>
    kbps >= 1024 ? `${(kbps / 1024).toFixed(0)} Mbps` : `${kbps} Kbps`;

  const fmtDuration = (minutes: number) => {
    if (minutes >= 1440) return `${(minutes / 1440).toFixed(0)} Days`;
    if (minutes >= 60) return `${(minutes / 60).toFixed(0)} Hours`;
    return `${minutes} Mins`;
  };

  const handleLogout = () => {
    sessionStorage.removeItem("hs_user");
    navigate(`/portal/login${queryStr}`);
  };

  const copyToClipboard = (value: string, field: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // 4. Load Data & Auto-Connect
  useEffect(() => {
    if (!user) {
      navigate(`/portal/register${queryStr}`);
      return;
    }

    const loadData = async () => {
      try {
        const phone = user.phone || user.username;
        const statusRes = await fetchData(
          `/portal/status?username=${encodeURIComponent(phone)}`,
          { auth: "none" },
        );

        if (statusRes.state === "ACTIVE_PLAN") {
          if (routerId) {
            const tokenRes = await fetchData("/portal/connect-token", {
              method: "POST",
              auth: "none",
              body: JSON.stringify({ user_id: user.user_id }),
            });
            redirectToMikrotikConnect(tokenRes.token);
            return;
          }
        }

        if (routerId) {
          const plansRes = await fetchData(`/portal/plans/${routerId}`, {
            auth: "none",
          });
          setPlans(plansRes);
        }
      } catch (err) {
        console.error("Failed to load plans", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 5. Verify Payment
  const handleVerifyPayment = async () => {
    if (!user || !user.user_id) {
      alert("Session expired. Please logout and login again.");
      return;
    }

    setVerifying(true);

    try {
      const phone = user.phone || user.username;
      const statusRes = await fetchData(
        `/portal/status?username=${encodeURIComponent(phone)}`,
        { auth: "none" },
      );

      if (statusRes.state === "ACTIVE_PLAN") {
        const tokenRes = await fetchData("/portal/connect-token", {
          method: "POST",
          auth: "none",
          body: JSON.stringify({ user_id: user.user_id }),
        });
        redirectToMikrotikConnect(tokenRes.token);
      } else {
        alert(
          "Payment not found yet. Please ensure you paid to Paybill 552800, Account 411591, then try again.",
        );
      }
    } catch (err: any) {
      alert(err.message || "Failed to verify payment. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Wifi className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-slate-800 text-lg tracking-tight hidden sm:block">
              Internet Portal
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
              <span className="text-sm font-bold text-slate-700">
                {user.phone || user.username}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-red-500 transition-all"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Select a Plan</h1>
          <p className="text-slate-500 text-sm mt-1">
            Choose a package, pay via M-Pesa, then verify your payment.
          </p>
        </div>

        {loading && (
          <div className="space-y-4 animate-pulse">
            <div className="bg-white h-32 rounded-2xl" />
            <div className="bg-white h-32 rounded-2xl" />
          </div>
        )}

        {!loading && plans.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <Signal className="h-6 w-6 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold">No Packages Found</h3>
          </div>
        )}

        {/* Plan Grid */}
        <div className="grid gap-4 sm:grid-cols-2 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan?.id === plan.id;
            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(isSelected ? null : plan)}
                className={`bg-white rounded-2xl p-5 shadow-sm border-2 transition-all cursor-pointer flex flex-col ${
                  isSelected
                    ? "border-blue-500 ring-2 ring-blue-100 shadow-md"
                    : "border-slate-200 hover:shadow-lg hover:border-slate-300"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {plan.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      <span className="text-xs font-medium text-slate-500">
                        Unlimited Data
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-2xl font-bold text-slate-900">
                      <span className="text-sm text-slate-400 mr-1">KES</span>
                      {plan.price}
                    </div>
                    {isSelected && (
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        Selected
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-semibold">
                      {fmtSpeed(plan.download_speed)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-semibold">
                      {fmtDuration(plan.duration_minutes)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Instructions — shown after selecting a plan */}
        {selectedPlan && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            {/* Banner */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-5 py-4">
              <div className="flex items-center gap-2 text-white">
                <CreditCard className="h-5 w-5" />
                <span className="font-bold text-base">
                  Pay via M-Pesa — KES {selectedPlan.price}
                </span>
              </div>
              <p className="text-green-100 text-xs mt-0.5">
                Follow the steps below to complete your payment
              </p>
            </div>

            <div className="p-5 space-y-4">
              {/* Steps */}
              <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
                <li>
                  Go to{" "}
                  <span className="font-semibold text-slate-800">M-Pesa</span>{" "}
                  on your phone
                </li>
                <li>
                  Select{" "}
                  <span className="font-semibold text-slate-800">
                    Lipa na M-Pesa → Paybill
                  </span>
                </li>
                <li>
                  Enter the details below and send{" "}
                  <span className="font-semibold text-slate-800">
                    KES {selectedPlan.price}
                  </span>
                </li>
              </ol>

              {/* Payment Details */}
              <div className="grid grid-cols-2 gap-3">
                {/* Paybill */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-medium">
                    Business No.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-extrabold text-slate-900 tracking-tight">
                      {PAYBILL}
                    </span>
                    <button
                      onClick={() => copyToClipboard(PAYBILL, "paybill")}
                      className="text-slate-400 hover:text-blue-500 transition-colors p-1"
                      title="Copy"
                    >
                      {copiedField === "paybill" ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Account */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-medium">
                    Account No.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-extrabold text-slate-900 tracking-tight">
                      {ACCOUNT}
                    </span>
                    <button
                      onClick={() => copyToClipboard(ACCOUNT, "account")}
                      className="text-slate-400 hover:text-blue-500 transition-colors p-1"
                      title="Copy"
                    >
                      {copiedField === "account" ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Amount reminder */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 font-medium text-center">
                Amount:{" "}
                <span className="font-extrabold">KES {selectedPlan.price}</span>
              </div>

              {/* Verify Button */}
              <button
                onClick={handleVerifyPayment}
                disabled={verifying}
                className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 active:scale-95 transition-all flex justify-center items-center gap-2 text-base shadow-md shadow-blue-200"
              >
                {verifying ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    Verifying Payment...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    I've Paid — Verify & Connect
                  </>
                )}
              </button>

              <p className="text-xs text-slate-400 text-center">
                After paying, tap the button above to verify and get connected.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalPlans;
